import React, { useState, useEffect } from 'react';
import { Button, Spinner, Text, MessageBar, MessageBarBody, Link } from '@fluentui/react-components';
import { ChevronDownRegular, ChevronUpRegular } from '@fluentui/react-icons';
import { EmailData, EmailSummary, SpoofDetectionResult, SpoofFactor } from '@shared/types/email';
import type { SummaryLength } from '@shared/types/user';
import { apiService } from '../services/apiService';

const CLASSIFICATION_LABELS: Record<string, string> = {
  informational: 'Informational',
  actionable: 'Actionable',
  response_required: 'Response required',
  follow_up: 'Follow-up',
};

/** When provided, summary uses these; when undefined, defaults to spoof on and short length. */
export interface SummaryPreferences {
  spoofDetection: boolean;
  summaryLength?: SummaryLength;
}

interface SummaryPanelProps {
  email: EmailData;
  summaryPreferences?: SummaryPreferences;
}

const SummaryPanel: React.FC<SummaryPanelProps> = ({ email, summaryPreferences }) => {
  const [summary, setSummary] = useState<EmailSummary | null>(null);
  const [spoofData, setSpoofData] = useState<SpoofDetectionResult | null>(null);
  const [factorsExpanded, setFactorsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const spoofOn = summaryPreferences?.spoofDetection ?? true;
  const lengthPref = summaryPreferences?.summaryLength ?? 'short';
  const apiLength = lengthPref === 'detailed' ? 'long' : 'short';

  const loadSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const summaryPromise = apiService.getEmailSummary(email.id, { length: apiLength });
      const spoofPromise = spoofOn ? apiService.detectSpoof(email.id) : Promise.resolve(null);
      const [summaryData, spoofResult] = await Promise.all([summaryPromise, spoofPromise]);
      setSummary(summaryData);
      setSpoofData(spoofResult ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email.id) {
      loadSummary();
    }
  }, [email.id, spoofOn, apiLength]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spinner label="Generating summary..." />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Text>{error}</Text>
        <Button onClick={loadSummary} style={{ marginTop: '10px' }}>
          Retry
        </Button>
      </div>
    );
  }

  if (!summary) {
    return (
      <div>
        <Text>Click the button below to generate a summary.</Text>
        <Button onClick={loadSummary} style={{ marginTop: '10px' }}>
          Generate Summary
        </Button>
      </div>
    );
  }

  const getRiskColor = (score: number) => {
    if (score <= 20) return '#107c10';
    if (score <= 60) return '#ffaa44';
    return '#d13438';
  };
  const getRiskLevel = (score: number) => {
    if (score <= 20) return 'Trusted';
    if (score <= 60) return 'Suspicious';
    if (score <= 80) return 'High Risk';
    return 'Critical';
  };

  return (
    <div>
      {/* Security at top: compact bar */}
      {spoofData && (
        <div style={{ marginBottom: '16px' }}>
          {spoofData.isTrustedSender && (
            <MessageBar intent="success" style={{ marginBottom: '8px' }}>
              <MessageBarBody>Trusted Sender</MessageBarBody>
            </MessageBar>
          )}
          {!spoofData.isTrustedSender && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                marginBottom: '8px',
              }}
            >
              <Text weight="semibold" size={300}>Security</Text>
              <div
                style={{
                  width: '80px',
                  height: '18px',
                  backgroundColor: '#e1dfdd',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${spoofData.spoofScore}%`,
                    height: '100%',
                    backgroundColor: getRiskColor(spoofData.spoofScore),
                  }}
                />
              </div>
              <Text size={200} style={{ color: getRiskColor(spoofData.spoofScore), fontWeight: 600 }}>
                {spoofData.spoofScore}/100 · {getRiskLevel(spoofData.spoofScore)}
              </Text>
              {spoofData.factors && spoofData.factors.length > 0 && (
                <Button
                  appearance="subtle"
                  size="small"
                  icon={factorsExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
                  onClick={() => setFactorsExpanded(!factorsExpanded)}
                >
                  {factorsExpanded ? 'Hide' : 'Show'} analysis factors
                </Button>
              )}
            </div>
          )}
          {factorsExpanded && spoofData.factors && spoofData.factors.length > 0 && (
            <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
              {spoofData.factors.map((factor: SpoofFactor, index: number) => (
                <div key={index} style={{ marginBottom: '6px' }}>
                  <Text weight="semibold" size={200}>{factor.name}: {factor.score}/100</Text>
                  <Text size={200} style={{ display: 'block', color: '#666' }}>{factor.details}</Text>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Classification */}
      <div style={{ marginBottom: '12px' }}>
        <Text
          size={200}
          style={{
            display: 'inline-block',
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: '#e8f4fc',
            color: '#005a9e',
            fontWeight: 600,
          }}
        >
          {CLASSIFICATION_LABELS[summary.classification ?? 'informational'] ?? 'Informational'}
        </Text>
      </div>

      <Text weight="semibold" size={400} style={{ display: 'block', marginBottom: '8px' }}>
        Summary
      </Text>
      <Text style={{ display: 'block', marginBottom: '16px', whiteSpace: 'pre-wrap' }}>
        {summary.summary}
      </Text>

      {summary.assistantContext && summary.assistantContext.trim() && (
        <MessageBar intent="info" style={{ marginBottom: '16px' }}>
          <MessageBarBody>
            <Text style={{ display: 'block', marginBottom: summary.assistantContextSources?.length ? '8px' : 0 }}>
              {summary.assistantContext}
            </Text>
            {summary.assistantContextSources && summary.assistantContextSources.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: '4px' }}>
                  Sources:
                </Text>
                {summary.assistantContextSources.map((src, idx) => (
                  <div key={idx} style={{ marginBottom: '4px' }}>
                    <Link href={src.url} target="_blank" rel="noopener noreferrer" inline>
                      {src.label}
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </MessageBarBody>
        </MessageBar>
      )}

      {summary.actionItemsForYou && summary.actionItemsForYou.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <Text weight="semibold" size={400} style={{ display: 'block', marginBottom: '8px' }}>
            For you
          </Text>
          <ul style={{ marginLeft: '20px' }}>
            {summary.actionItemsForYou.map((item: string, index: number) => (
              <li key={index}>
                <Text>{item}</Text>
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary.keyPoints && summary.keyPoints.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <Text weight="semibold" size={400} style={{ display: 'block', marginBottom: '8px' }}>
            Key Points
          </Text>
          <ul style={{ marginLeft: '20px' }}>
            {summary.keyPoints.map((point: string, index: number) => (
              <li key={index}>
                <Text>{point}</Text>
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary.actionItems && summary.actionItems.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <Text weight="semibold" size={400} style={{ display: 'block', marginBottom: '8px' }}>
            Action Items
          </Text>
          <ul style={{ marginLeft: '20px' }}>
            {summary.actionItems.map((item: string, index: number) => (
              <li key={index}>
                <Text>{item}</Text>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Text size={200} style={{ display: 'block', marginTop: '16px', color: '#666' }}>
        Generated using {summary.model === 'copilot' ? 'Microsoft 365 Copilot' : summary.model === 'copilotChat' ? 'Microsoft Copilot (Chat)' : 'Azure OpenAI'}
      </Text>
    </div>
  );
};

export default SummaryPanel;
